import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Chip, Card, CardContent, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { QuestionHistory } from '../services/quizAssignment.service';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface QuestionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  questionText: string;
  history: QuestionHistory[];
}

export default function QuestionHistoryModal({ open, onClose, questionText, history }: QuestionHistoryModalProps) {
  // Sort history by date (most recent first)
  const sortedHistory = [...history].sort((a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h6" component="div">
              Historique de la question
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {questionText}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {sortedHistory.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography variant="body1" color="text.secondary">
              Aucun historique disponible pour cette question
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {sortedHistory.map((item, index) => (
              <Card
                key={item.id}
                sx={{
                  mb: 2,
                  borderLeft: item.was_correct ? '4px solid #2e7d32' : '4px solid #d32f2f',
                  boxShadow: item.was_correct
                    ? '0 2px 8px rgba(46, 125, 50, 0.15)'
                    : '0 2px 8px rgba(211, 47, 47, 0.15)'
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {item.was_correct ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                      <Chip
                        label={item.was_correct ? 'Correcte' : 'Incorrecte'}
                        color={item.was_correct ? 'success' : 'error'}
                        size="small"
                      />
                      {item.score !== null && (
                        <Chip
                          label={`Score: ${item.score}%`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" display="block" fontWeight="bold">
                        {format(new Date(item.date), 'dd MMM yyyy', { locale: fr })}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {format(new Date(item.date), 'HH:mm')}
                      </Typography>
                    </Box>
                  </Box>

                  {item.student_answer && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Réponse:</strong> {item.student_answer}
                      </Typography>
                    </>
                  )}

                  {item.feedback && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Feedback:</strong> {item.feedback}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
